import React from 'react';
import { formatCurrency } from '../utils/currency';

const SvgIcon = ({ children, className = 'w-4 h-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    {children}
  </svg>
);

const EditSvg = () => (
  <SvgIcon>
    <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
    <path fillRule="evenodd" d="M2 15a3 3 0 003 3h10a1 1 0 100-2H5a1 1 0 01-1-1V5a1 1 0 011-1h6a1 1 0 100-2H5a3 3 0 00-3 3v10z" clipRule="evenodd" />
  </SvgIcon>
);

const ViewSvg = () => (
  <SvgIcon>
    <path d="M10 3c-4.418 0-8 4.03-8 7s3.582 7 8 7 8-4.03 8-7-3.582-7-8-7zm0 12a5 5 0 110-10 5 5 0 010 10z" />
    <path d="M10 7a3 3 0 100 6 3 3 0 000-6z" />
  </SvgIcon>
);

const PaymentSvg = () => (
  <SvgIcon>
    <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2H2V5z" />
    <path fillRule="evenodd" d="M2 9h16v6a2 2 0 01-2 2H4a2 2 0 01-2-2V9zm4 3a1 1 0 000 2h4a1 1 0 100-2H6z" clipRule="evenodd" />
  </SvgIcon>
);

const WriteOffSvg = () => (
  <SvgIcon>
    <path d="M10 18a8 8 0 100-16 8 8 0 000 16z" />
    <path fillRule="evenodd" d="M6.707 6.707a1 1 0 010-1.414 1 1 0 011.414 0L10 7.172l1.879-1.879a1 1 0 011.414 0 1 1 0 010 1.414L11.414 8.586l1.879 1.879a1 1 0 01-1.414 1.414L10 10l-1.879 1.879a1 1 0 01-1.414-1.414l1.879-1.879-1.879-1.879z" clipRule="evenodd" />
  </SvgIcon>
);

const PdfSvg = () => (
  <SvgIcon>
    <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V7.414A2 2 0 0017.414 6l-4-4A2 2 0 0012 1.586V2H4zm8 1.414V6a2 2 0 002 2h2.586L12 3.414z" clipRule="evenodd" />
    <path d="M6 10h2a2 2 0 010 4H6v-4zm2 2H7v1h1a1 1 0 000-2zM10 10h2a2 2 0 010 4h-2v-4zm2 2h-1v1h1a1 1 0 000-2zM14 10h2v1h-1v1h1v1h-1v1h-1v-4z" />
  </SvgIcon>
);

const TrashSvg = () => (
  <SvgIcon>
    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 100 2h1v10a2 2 0 002 2h8a2 2 0 002-2V6h1a1 1 0 100-2h-2V3a1 1 0 00-1-1H6zm2 6a1 1 0 012 0v7a1 1 0 11-2 0V8zm4 0a1 1 0 10-2 0v7a1 1 0 102 0V8z" clipRule="evenodd" />
  </SvgIcon>
);

export default function InvoiceList({
  filteredInvoices,
  invoicesViewMode,
  setInvoicesViewMode,
  clients,
  cases,
  openEditInvoiceModal,
  openDetailsModal,
  openRecordPaymentModal,
  openWriteOffModal,
  handleGeneratePdf,
  handleDelete,
  fetchInvoices
}) {
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
        <h5 className="text-lg font-bold text-gray-800">All Invoices</h5>
      </div>
      <div className="p-6">
        {invoicesViewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">No invoices found.</td>
                  </tr>
                ) : (
                  filteredInvoices.map(invoice => {
                    const client = clients.find(c => c.id === invoice.client);
                    const clientName = invoice.client_full_name || (client ? (client.first_name ? `${client.first_name} ${client.last_name}` : client.organization_name) : 'N/A');
                    const caseTitle = invoice.case_title || cases.find(c => c.id === invoice.case)?.title || 'N/A';
                    const totalAmount = invoice.total_amount || 0;
                    const balanceDue = invoice.balance_due || 0;

                    return (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{clientName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseTitle}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.issue_date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.due_date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(totalAmount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(balanceDue)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                            invoice.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                            invoice.status === 'Partial' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button title="Edit Invoice" aria-label="Edit Invoice" className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100" onClick={() => openEditInvoiceModal(invoice)}><EditSvg /></button>
                          <button title="View Details" aria-label="View Details" className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100" onClick={() => openDetailsModal(invoice)}><ViewSvg /></button>
                          <button title="Record Payment" aria-label="Record Payment" className="text-green-600 hover:text-green-900 p-2 rounded-full hover:bg-green-100" onClick={() => openRecordPaymentModal(invoice)}><PaymentSvg /></button>
                          <button title="Write Off" aria-label="Write Off" className="text-amber-600 hover:text-amber-900 p-2 rounded-full hover:bg-amber-100" onClick={() => openWriteOffModal(invoice)}><WriteOffSvg /></button>
                          <button title="Download PDF" aria-label="Download PDF" className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-100" onClick={() => handleGeneratePdf(invoice.id)}><PdfSvg /></button>
                          <button title="Delete Invoice" aria-label="Delete Invoice" className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100" onClick={() => handleDelete('invoices', invoice.id, 'Invoice', fetchInvoices)}><TrashSvg /></button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map(invoice => {
                const client = clients.find(c => c.id === invoice.client);
                const clientName = client ? (client.first_name ? `${client.first_name} ${client.last_name}` : client.organization_name) : 'N/A';
                const caseTitle = invoice.case_title || cases.find(c => c.id === invoice.case)?.title || 'N/A';
                const totalAmount = invoice.total_amount || 0;
                const balanceDue = invoice.balance_due || 0;

                return (
                  <div key={invoice.id} className="bg-white shadow-md rounded-lg overflow-hidden">
                    <div className="p-6">
                      <div className="flex justify-between items-start">
                        <h5 className="text-lg font-bold text-gray-800">Invoice #{invoice.invoice_number}</h5>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                          invoice.status === 'Partial' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2"><strong>Client:</strong> {invoice.client_full_name || clientName}</p>
                      <p className="text-sm text-gray-600"><strong>Case:</strong> {caseTitle}</p>
                      <p className="text-sm text-gray-600"><strong>Due:</strong> {invoice.due_date}</p>
                      <p className="text-2xl font-bold text-gray-800 mt-4">{formatCurrency(balanceDue)} <span className="text-sm font-normal">due</span></p>
                      <p className="text-sm text-gray-500">Total: {formatCurrency(totalAmount)}</p>
                      <div className="mt-6 flex justify-end space-x-2">
                        <button title="Edit Invoice" aria-label="Edit Invoice" className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100" onClick={() => openEditInvoiceModal(invoice)}><EditSvg /></button>
                        <button title="View Details" aria-label="View Details" className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100" onClick={() => openDetailsModal(invoice)}><ViewSvg /></button>
                        <button title="Record Payment" aria-label="Record Payment" className="text-green-600 hover:text-green-900 p-2 rounded-full hover:bg-green-100" onClick={() => openRecordPaymentModal(invoice)}><PaymentSvg /></button>
                        <button title="Write Off" aria-label="Write Off" className="text-amber-600 hover:text-amber-900 p-2 rounded-full hover:bg-amber-100" onClick={() => openWriteOffModal(invoice)}><WriteOffSvg /></button>
                        <button title="Download PDF" aria-label="Download PDF" className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-100" onClick={() => handleGeneratePdf(invoice.id)}><PdfSvg /></button>
                        <button title="Delete Invoice" aria-label="Delete Invoice" className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100" onClick={() => handleDelete('invoices', invoice.id, 'Invoice', fetchInvoices)}><TrashSvg /></button>
                      </div>
                    </div>
                  </div>
                );
              })) : (
                <div className="col-span-full text-center py-4">
                  <p className="text-gray-500">No invoices found.</p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

